import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'auth/AuthProvider';
import { PERMISSIONS } from 'auth/permissions';
import { queryKeys } from 'core/api/queryKeys';
import { ApiError } from 'core/errors/ApiError';
import { humanizeApiError } from 'core/errors/mapFieldErrors';
import { useI18n } from 'core/i18n/I18nProvider';
import { paths } from 'core/routing/paths';
import { SpecialistAvatar } from 'features/career-specialists/SpecialistAvatar';
import {
  SpecialistStatusBadge,
  specialistActivationActionLabel,
} from 'features/career-specialists/SpecialistStatusBadge';
import {
  applySpecialistListSnapshots,
  filterSpecialistsByStatus,
  invalidateSpecialistQueries,
  mergeSpecialistIntoListPayload,
  normalizeSpecialist,
  normalizeSpecialistList,
  snapshotSpecialistQueries,
  sortSpecialists,
} from 'features/career-specialists/specialists.helpers';
import { operationsApi } from 'features/operations/operations.api';
import { EmptyState, ErrorState, PageHeader, Skeleton } from 'shared/components/Feedback';
import { detailsAction, RowActionsCell, RowActionsHeader } from 'shared/components/RowActionsMenu';
import { useConfirm } from 'shared/components/ConfirmDialog';
import { useToast } from 'shared/components/Toast';
import { localizedName } from 'shared/utilities/format';

function listFiltersForStatus(status) {
  if (status === 'active') {
    return { is_active: true };
  }
  if (status === 'inactive') {
    return { is_active: false };
  }
  return null;
}

export function SpecialistsPage() {
  const { t, locale } = useI18n();
  const { hasPermission } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const canManage = hasPermission(PERMISSIONS.specialistsManage);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [statusAnnouncement, setStatusAnnouncement] = useState('');

  const serverFilters = listFiltersForStatus(status);
  const queryKey = queryKeys.specialists(serverFilters);

  const query = useQuery({
    queryKey,
    queryFn: ({ signal }) => operationsApi.listSpecialists(serverFilters || undefined, { signal }),
    select: (data) => sortSpecialists(normalizeSpecialistList(data)),
  });

  const activation = useMutation({
    mutationFn: ({ id, isActive }) => operationsApi.setSpecialistActivation(id, isActive),
    onMutate: async ({ id, isActive }) => {
      await queryClient.cancelQueries({ queryKey: ['operations', 'specialists'] });
      const snapshots = snapshotSpecialistQueries(queryClient);
      const optimistic = normalizeSpecialist({ id, is_active: isActive });
      queryClient.setQueriesData({ queryKey: ['operations', 'specialists'] }, (current) => (
        mergeSpecialistIntoListPayload(current, optimistic)
      ));
      queryClient.setQueryData(queryKeys.specialist(id), (current) => (
        current && typeof current === 'object'
          ? { ...current, is_active: isActive }
          : current
      ));
      return { snapshots };
    },
    onSuccess: async (data, { id, isActive }) => {
      const specialist = normalizeSpecialist(data);
      if (specialist) {
        queryClient.setQueryData(queryKeys.specialist(String(id)), specialist);
        queryClient.setQueriesData({ queryKey: ['operations', 'specialists'] }, (current) => (
          mergeSpecialistIntoListPayload(current, specialist)
        ));
      }
      await invalidateSpecialistQueries(queryClient, id);
      const message = isActive ? t('specialists.statusActivated') : t('specialists.statusDeactivated');
      setStatusAnnouncement(message);
      toast.success(message);
    },
    onError: (error, _variables, context) => {
      if (context?.snapshots) {
        applySpecialistListSnapshots(queryClient, context.snapshots);
      }
      if (error instanceof ApiError && error.status === 403) {
        toast.error(t('errors.forbidden'));
        return;
      }
      toast.error(humanizeApiError(error, t));
    },
  });

  const removal = useMutation({
    mutationFn: (id) => operationsApi.deleteSpecialist(id),
    onSuccess: async () => {
      await invalidateSpecialistQueries(queryClient);
      toast.success(t('status.deleted'));
    },
    onError: (error) => {
      toast.error(error instanceof ApiError && error.isConflict()
        ? t('specialists.cannotDelete')
        : humanizeApiError(error, t));
    },
  });

  const items = useMemo(() => {
    const filtered = filterSpecialistsByStatus(query.data || [], status);
    const term = search.trim().toLowerCase();
    if (!term) {
      return filtered;
    }
    return filtered.filter((item) => `${item.name_ar || ''} ${item.name_en || ''} ${item.name || ''} ${item.title_ar || ''} ${item.title_en || ''}`
      .toLowerCase()
      .includes(term));
  }, [query.data, search, status]);

  if (query.isLoading) {
    return <Skeleton rows={6} />;
  }
  if (query.isError) {
    return <ErrorState title={humanizeApiError(query.error, t)} onRetry={query.refetch} retryLabel={t('actions.retry')} />;
  }

  return (
    <div>
      <div className="sr-only" aria-live="polite" aria-atomic="true">{statusAnnouncement}</div>
      <PageHeader
        title={t('specialists.title')}
        actions={canManage ? <Link className="btn btn-primary" to={paths.specialistCreate}>{t('specialists.create')}</Link> : null}
      />
      <div className="toolbar">
        <input className="input" value={search} onChange={(event) => setSearch(event.target.value)} placeholder={t('specialists.search')} aria-label={t('specialists.search')} />
        <select className="input" value={status} onChange={(event) => setStatus(event.target.value)} aria-label={t('specialists.statusFilter')}>
          <option value="all">{t('filters.all')}</option>
          <option value="active">{t('filters.active')}</option>
          <option value="inactive">{t('filters.inactive')}</option>
        </select>
      </div>
      {items.length === 0 ? <EmptyState title={t('empty.specialists')} /> : (
        <div className="table-wrap">
          <table className="data-table has-sticky-actions">
            <thead>
              <tr>
                <th>{t('specialists.detail')}</th>
                <th>{t('specialists.titleAr')}</th>
                <th>{t('specialists.languages')}</th>
                <th>{t('specialists.timezone')}</th>
                <th>{t('specialists.active')}</th>
                <RowActionsHeader />
              </tr>
            </thead>
            <tbody>
              {items.map((specialist) => {
                const name = localizedName(specialist, locale) || specialist.name_en || specialist.name_ar;
                const canToggle = specialist.is_active === true || specialist.is_active === false;
                return (
                  <tr key={specialist.id}>
                    <td>
                      <div className="avatar-row">
                        <SpecialistAvatar specialist={specialist} lazy />
                        <span>{name}</span>
                      </div>
                    </td>
                    <td>{locale === 'ar' ? specialist.title_ar || specialist.title : specialist.title_en || specialist.title}</td>
                    <td>{(specialist.languages || []).join(', ') || '—'}</td>
                    <td>{specialist.timezone || '—'}</td>
                    <td><SpecialistStatusBadge isActive={specialist.is_active} /></td>
                    <RowActionsCell
                      label={`${t('actions.rowActions')} #${specialist.id}`}
                      items={[
                        detailsAction(paths.specialistDetail(specialist.id), t),
                        canManage ? { key: 'edit', label: t('actions.edit'), to: paths.specialistEdit(specialist.id) } : null,
                        canManage ? {
                          key: 'activation',
                          label: specialistActivationActionLabel(specialist.is_active, t),
                          disabled: activation.isPending || !canToggle,
                          onClick: () => {
                            if (!canToggle) {
                              return;
                            }
                            activation.mutate({ id: specialist.id, isActive: specialist.is_active !== true });
                          },
                        } : null,
                        canManage ? {
                          key: 'delete',
                          label: t('actions.delete'),
                          tone: 'danger',
                          disabled: removal.isPending,
                          onClick: async () => {
                            if (await confirm({ body: t('confirm.delete') })) {
                              removal.mutate(specialist.id);
                            }
                          },
                        } : null,
                      ]}
                    />
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
